import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { signIn, signInWithGoogle } from "../../redux/actions/authActions";
import SignInLayout from "../../layouts/SignInLayout";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import { useTranslation } from "react-i18next";

import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  TextField as MuiTextField,
  Typography,
  Grid,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";
import { Link } from "react-router-dom";
import {
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  LockOutlined as LockOutlinedIcon,
  TrendingUpOutlined as TrendingUpOutlinedIcon,
} from "@material-ui/icons";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Shell = styled.div`
  width: 100%;
`;

const HeroGridItem = styled(Grid)`
  ${(props) => props.theme.breakpoints.down("sm")} {
    order: 2;
  }
`;

const LoginGridItem = styled(Grid)`
  ${(props) => props.theme.breakpoints.down("sm")} {
    order: 1;
  }
`;

const HeroPanel = styled(Paper)`
  padding: 40px;
  min-height: 100%;
  border-radius: 30px 0 0 30px;
  background: linear-gradient(155deg, #0f2948 0%, #1d4e89 58%, #67b5de 100%);
  color: #fff;
  box-shadow: 0 26px 60px rgba(15, 23, 42, 0.18);

  ${(props) => props.theme.breakpoints.down("sm")} {
    border-radius: 0 0 30px 30px;
    padding: 30px 24px;
  }
`;

const LoginPanel = styled(Paper)`
  padding: 40px;
  border-radius: 0 30px 30px 0;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 26px 60px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(10px);

  ${(props) => props.theme.breakpoints.down("sm")} {
    border-radius: 30px 30px 0 0;
    padding: 30px 24px;
  }
`;

const Frame = styled.div`
  overflow: hidden;
  border-radius: 32px;
`;

const BigAvatar = styled(Avatar)`
  width: 88px;
  height: 88px;
  text-align: center;
  margin: 0 auto 24px;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
`;

const HeroKicker = styled(Typography)`
  opacity: 0.76;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.74rem;
  font-weight: 700;
`;

const HeroTitle = styled(Typography)`
  font-weight: 800;
  margin-top: 18px;
`;

const HeroDescription = styled(Typography)`
  opacity: 0.9;
  margin-top: 12px;
  max-width: 360px;
`;

const HighlightCard = styled.div`
  margin-top: 28px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
`;

const PanelEyebrow = styled(Typography)`
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.72rem;
  font-weight: 700;
`;

const PanelTitle = styled(Typography)`
  color: #10213a;
  font-weight: 800;
  margin-top: 8px;
`;

const PanelDescription = styled(Typography)`
  color: #64748b;
  margin-top: 8px;
`;

const SubmitButton = styled(Button)`
  && {
    margin-top: 12px;
    border-radius: 14px;
    padding: 12px 18px;
    text-transform: none;
    font-weight: 700;
    box-shadow: 0 16px 28px rgba(29, 78, 137, 0.18);
  }
`;

const ForgotButton = styled(Button)`
  && {
    margin-top: 8px;
    text-transform: none;
    font-weight: 700;
  }
`;

function SignIn() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [googleError, setGoogleError] = useState("");

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setGoogleError("");

      try {
        const response = await dispatch(signInWithGoogle(credential));

        if (response?.token) {
          history.push("/dashboard");
          return;
        }

        if (response?.status === "ACCOUNT_EXISTS_REQUIRES_LINK") {
          setGoogleError(response.message || t("auth.invalidCredentials"));
          return;
        }

        if (response?.status === "REGISTRATION_REQUIRED") {
          setGoogleError(response.message || t("auth.unauthorized"));
          return;
        }

        setGoogleError(t("auth.googleError"));
      } catch (error) {
        setGoogleError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            t("auth.googleError")
        );
      }
    },
    [dispatch, history, t]
  );

  return (
    <SignInLayout>
      <Shell>
        <Helmet title={t("auth.signIn")} />
        <Frame>
          <Grid container>
            <HeroGridItem item xs={12} md={5}>
              <HeroPanel elevation={0}>
                <HeroKicker variant="body2">
                  {t("auth.accessKicker")}
                </HeroKicker>
                <HeroTitle variant="h3">{t("auth.heroTitle")}</HeroTitle>
                <HeroDescription variant="body1">
                  {t("auth.heroDescription")}
                </HeroDescription>

                <HighlightCard>
                  <Box display="flex" alignItems="center" gridGap={12}>
                    <Avatar style={{ background: "rgba(255,255,255,0.16)" }}>
                      <TrendingUpOutlinedIcon />
                    </Avatar>
                    <div>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 700 }}
                      >
                        {t("auth.centralizedTitle")}
                      </Typography>
                      <Typography variant="body2" style={{ opacity: 0.84 }}>
                        {t("auth.centralizedDescription")}
                      </Typography>
                    </div>
                  </Box>
                </HighlightCard>

                <HighlightCard>
                  <Box display="flex" alignItems="center" gridGap={12}>
                    <Avatar style={{ background: "rgba(255,255,255,0.16)" }}>
                      <LockOutlinedIcon />
                    </Avatar>
                    <div>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 700 }}
                      >
                        {t("auth.secureTitle")}
                      </Typography>
                      <Typography variant="body2" style={{ opacity: 0.84 }}>
                        {t("auth.secureDescription")}
                      </Typography>
                    </div>
                  </Box>
                </HighlightCard>
              </HeroPanel>
            </HeroGridItem>

            <LoginGridItem item xs={12} md={7}>
              <LoginPanel elevation={0}>
                <BigAvatar alt="Wifiads" src="/static/img/avatars/logo.png" />
                <PanelEyebrow variant="body2" align="center">
                  {t("auth.systemAccess")}
                </PanelEyebrow>
                <PanelTitle component="h2" variant="h4" align="center">
                  {t("auth.productTitle")}
                </PanelTitle>
                <PanelDescription
                  component="h2"
                  variant="body1"
                  align="center"
                  gutterBottom
                >
                  {t("auth.continueAccount")}
                </PanelDescription>

                <Formik
                  initialValues={{
                    email: "",
                    password: "",
                    submit: false,
                  }}
                  validationSchema={Yup.object().shape({
                    email: Yup.string()
                      .email(t("auth.invalidEmail"))
                      .max(255)
                      .required(t("auth.emailRequired")),
                    password: Yup.string()
                      .max(255)
                      .required(t("auth.passwordRequired")),
                  })}
                  onSubmit={async (
                    values,
                    { setErrors, setStatus, setSubmitting }
                  ) => {
                    try {
                      await dispatch(
                        signIn({
                          email: values.email,
                          password: values.password,
                        })
                      );
                      history.push("/dashboard");
                    } catch (error) {
                      const message =
                        error?.response?.status === 403
                          ? t("auth.unauthorized")
                          : t("auth.invalidCredentials");

                      setStatus({ success: false });
                      setErrors({ submit: message });
                      setSubmitting(false);
                    }
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                  }) => (
                    <form noValidate onSubmit={handleSubmit}>
                      {googleError && (
                        <Alert mt={3} mb={1} severity="warning">
                          {googleError}
                        </Alert>
                      )}
                      {errors.submit && (
                        <Alert mt={3} mb={1} severity="warning">
                          {errors.submit}
                        </Alert>
                      )}
                      <TextField
                        type="email"
                        name="email"
                        label={t("auth.email")}
                        value={values.email}
                        error={Boolean(touched.email && errors.email)}
                        fullWidth
                        helperText={touched.email && errors.email}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={2}
                        variant="outlined"
                      />
                      <TextField
                        type="password"
                        name="password"
                        label={t("auth.password")}
                        value={values.password}
                        error={Boolean(touched.password && errors.password)}
                        fullWidth
                        helperText={touched.password && errors.password}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={2}
                        variant="outlined"
                      />
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        mt={1}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox value="remember" color="primary" />
                          }
                          label={t("auth.rememberMe")}
                        />
                      </Box>
                      <SubmitButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        endIcon={<ArrowForwardRoundedIcon />}
                      >
                        {t("auth.signIn")}
                      </SubmitButton>
                      <Link to="/auth/reset-password">
                        <ForgotButton color="primary" fullWidth>
                          {t("auth.forgotPassword")}
                        </ForgotButton>
                      </Link>
                      <GoogleSignInButton
                        onCredential={handleGoogleCredential}
                        title={t("auth.continueWithGoogle")}
                      />
                    </form>
                  )}
                </Formik>
              </LoginPanel>
            </LoginGridItem>
          </Grid>
        </Frame>
      </Shell>
    </SignInLayout>
  );
}

export default SignIn;
